import { useDelivery } from 'api/hooks/useDelivery';
import { useMemo } from 'react';
import { useTurnaroundOriginDeliveryId } from './useTurnaroundOriginDeliveryId';
import { TURNAROUND_PHASE_3_FEATURE_FLAG } from 'featureFlags';
import { useTurnaroundDestinationEntities } from './useTurnaroundDestinationEntities';

export const useTurnaroundOriginEntities = () => {
  const [turnaroundOriginDeliveryId] = useTurnaroundOriginDeliveryId();
  const [turnaroundDestinationEntities] = useTurnaroundDestinationEntities();

  const { data: delivery, isLoading } = useDelivery(turnaroundOriginDeliveryId);

  // filter out relevant sets
  // sort the sets by displayCode
  const sets = useMemo(
    () =>
      delivery?.sets
        ? delivery?.sets
            .filter(set => {
              const isSetSelectedForDestination = turnaroundDestinationEntities.some(
                entity => entity.type === 'set' && entity.setKitInstanceId === set.kitInstanceId,
              );

              if (isSetSelectedForDestination) {
                return false;
              }

              // If the set has a turnaroundOrigin, it should not be filtered out if the turnaroundStage is PENDING
              if (TURNAROUND_PHASE_3_FEATURE_FLAG && set.turnaroundOrigin && set.turnaroundStage === 'PENDING') {
                return true;
              }

              return !set.turnaroundOrigin && !set.turnaroundDestination;
            })
            .sort((a, b) => a.displayCode.localeCompare(b.displayCode))
        : [],
    [delivery?.sets, turnaroundDestinationEntities],
  );

  const implants = useMemo(() => {
    return delivery?.implants
      ? delivery?.implants
          .filter(implant => {
            const areImplantsSelectedForDestination = turnaroundDestinationEntities.some(
              entity => entity.type === 'implant' && entity.implantInstanceId === implant.implantInstanceId,
            );

            if (areImplantsSelectedForDestination) {
              return false;
            }

            return !implant.turnaroundOrigin && !implant.turnaroundDestination;
          })
          .sort((a, b) => {
            const aDescription = a.gs1Data?.tradeItemDescription;
            const bDescription = b.gs1Data?.tradeItemDescription;

            if (aDescription && bDescription) {
              return aDescription.localeCompare(bDescription);
            }

            return a.barcode.localeCompare(b.barcode);
          })
      : [];
  }, [delivery?.implants, turnaroundDestinationEntities]);

  return {
    deliveryStatus: delivery?.status,
    sets,
    implants,
    isLoading,
  };
};
